import axios from 'axios';
import api from '~/services/api';
import { userHasNoPhone } from '~/utils/phoneValidation';
import {
  MOBILE_PHONE_ERROR_KEYS,
  SaveMobilePhoneRequest,
  SendCodeRequest,
  UserDetailsResponse,
  ValidateCodeRequest,
  ValidateCodeResponse
} from './types';

export { MOBILE_PHONE_ERROR_KEYS, userHasNoPhone };
export type { SaveMobilePhoneRequest, SendCodeRequest, UserDetailsResponse, ValidateCodeRequest, ValidateCodeResponse };

// Função utilitária para limpar o documento removendo pontos, hífens e espaços
const cleanDocument = (document: string): string => {
  return document.replace(/[.\-\s]/g, '');
};

/**
 * Consulta o cadastro pelo CPF. O 404 é a resposta normal para CPF sem cadastro: devolve `null`, e a tela segue
 * para o preenchimento manual sem aviso. Só falha real (5xx, rede) sobe como erro.
 */
export const getUserDetails = async (document: string): Promise<UserDetailsResponse | null> => {
  const cleanedDocument = cleanDocument(document);
  try {
    const response = await api.v2?.get(`/users/details/${cleanedDocument}`);
    // A API retorna { data: { name, document_number, masked_email, masked_phone, has_phone?, has_email? }, notifications: [] }
    return response?.data?.data ?? null;
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    throw error;
  }
};

export const sendCode = async (document: string, codeType: number): Promise<any> => {
  const cleanedDocument = cleanDocument(document);
  const requestBody: SendCodeRequest = {
    document: cleanedDocument,
    codeType
  };

  const response = await api.v2!.post('/auth/send-code', requestBody);

  return response.data;
};

export const validateCode = async (
  document: string,
  code: string,
  codeType: number,
  organizationId?: string
): Promise<ValidateCodeResponse> => {
  const cleanedDocument = cleanDocument(document);
  const requestBody: ValidateCodeRequest = {
    document: cleanedDocument,
    code,
    codeType
  };

  const response = await api.v2!.post<{ data: ValidateCodeResponse; notifications?: unknown[] }>(
    '/auth/validate-code',
    requestBody,
    { params: organizationId ? { organization_id: organizationId } : undefined }
  );

  // A API retorna { data: { person_id, user_id, token, ... }, notifications: [] }
  const payload = response.data?.data ?? response.data;
  return payload as ValidateCodeResponse;
};

/**
 * Grava o celular de quem ainda não tem telefone (CORE-1025): `PUT /v2/users/me/mobile-phone`.
 * O usuário vem do token devolvido pelo `validate-code`, então a chamada não passa pela instância `api.v2`
 * (o interceptor dela troca o Bearer pelo client credential e, no 401, renova e repete com ele).
 * Erros (400 INVALID_MOBILE_PHONE / MOBILE_PHONE_ALREADY_REGISTERED, 401, 404) sobem para a tela tratar.
 */
export const saveMobilePhone = async (mobilePhone: string, token: string): Promise<boolean> => {
  const requestBody: SaveMobilePhoneRequest = { mobile_phone: mobilePhone };

  const response = await axios.put<{ data: boolean; notifications?: unknown[] }>(
    '/users/me/mobile-phone',
    requestBody,
    { baseURL: api.v2?.defaults.baseURL, headers: { Authorization: `Bearer ${token}` } }
  );

  return response.data?.data === true;
};
